<template>
    <div class="last-step">
        <div class="ls-card">
            <div class="ls-label">{{ durationText }}</div>
            <kimsa-text-field :placeholder="$t('common.write_here')" type="number" input-mode="numeric"
                :value="duration" :suffix="$t('common.short_minutes')" @change="duration = $event" centered-value />
        </div>

        <div class="ls-card">
            <div class="ls-label">{{ $t('exercises.effort') }}</div>
            <kimsa-rpe-slider :value="effort" :min="0" :max="10" :label="effortLabel"
                @change="effort = `${$event}`" ref="effort" />
        </div>

        <div class="ls-card" v-if="isCompetence">
            <div class="ls-label">{{ $t('common.position') }}</div>
            <kimsa-text-field :placeholder="$t('common.write_here')" :value="position" @change="position = $event" />
            <div class="ls-label mt-4">{{ $t('common.team') }}</div>
            <kimsa-text-field :placeholder="$t('common.write_here')" :value="team" @change="team = $event" />
        </div>

        <div class="ls-card">
            <div class="ls-label">{{ $t('common.comment') }}</div>
            <kimsa-text-field :placeholder="$t('common.write_here')" :value="comment" @change="comment = $event" />
            <v-btn class="black--text finish-btn mt-4" @click="finish" block color="primary" depressed large
                :loading="loading" :disabled="!duration">
                {{ finishText }}
            </v-btn>
        </div>
    </div>
</template>

<script>

import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaRpeSlider from "@/components/common/inputs/KimsaRpeSlider";
import { mapActions, mapState } from "vuex";
import { updateCounters } from "../../utils/Routine";
import { sendMessage } from "../../utils/Messages";

export default {
    name: "PostAssignedRoutine",
    components: { KimsaRpeSlider, KimsaTextField },
    props: {
        assignedRoutineId: {
            type: String,
            default: '',
        },
        coachId: {
            type: String,
            default: null,
        },
        isCompetence: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            loadingContent: false,
            loading: false,
            routine: null,
            duration: null,
            effort: '5',
            comment: '',
            position: '',
            team: '',
        }
    },
    mounted() {
        const vm = this
        vm.setDefaults()
    },
    computed: {
        ...mapState(['currentRoutine']),
        effortLabel(vm = this) {
            switch (vm.effort) {
                case '0':
                    return vm.$t('routines.minimum')
                case '1':
                case '2':
                    return vm.$t('routines.lightweight')
                case '3':
                case '4':
                    return vm.$t('routines.moderate')
                case '5':
                case '6':
                    return vm.$t('routines.hard')
                case '7':
                case '8':
                    return vm.$t('routines.very_hard')
                case '9':
                    return vm.$t('routines.almost_maximum')
                case '10':
                    return vm.$t('routines.maximum')
            }
        },
        studentId(vm = this) {
            return vm.currentUser?.uid || null
        },
        charge(vm = this) {
            return Number(vm.duration || 0) * Number(vm.effort || 0)
        },
        durationText(vm = this) {
            if (vm.isCompetence) return vm.$t('common.duration_competence')
            return vm.$t('routines.duration')
        },
        finishText(vm = this) {
            if (vm.isCompetence) return vm.$t('common.finish_competence')
            return vm.$t('routines.finish_training')
        },
    },
    methods: {
        ...mapActions(['bindUserAccount']),
        async finish() {
            const vm = this
            await vm.withLoading(async () => {
                let payload = {
                    duration: vm.duration,
                    effort: vm.effort,
                    comment: vm.comment
                }

                if (vm.isCompetence) {
                    payload.position = vm.position
                    payload.team = vm.team
                }

                const saved = await vm.saveAssignedRoutine(vm.assignedRoutineId, {
                    'advance.post': true,
                    'advance.postInfo': payload
                })
                if (!saved) return

                await updateCounters(vm.duration)
                await vm.sendMessageToCoach()

                vm.$emit('finish')
            })
        },
        async sendMessageToCoach() {
            const vm = this

            let message = `${vm.$t('common.date')}: ${vm.$moment().format('DD-MM-YYYY')} \n${vm.$t('routines.duration')}: ${vm.duration}${vm.$t('common.short_minutes')} \n${vm.$t('exercises.effort')}: ${vm.effort} \n${vm.$t('exercises.charge')}: ${vm.charge}`
            if (vm.position) message += `\n${vm.$t('common.position')}: ${vm.position}`
            if (vm.team) message += `\n${vm.$t('common.team')}: ${vm.team}`
            if (vm.comment) message += `\n${vm.$t('common.comment')}: ${vm.comment}`

            let payload = {
                message: message,
                from: vm.studentId,
                assignedRoutineId: vm.assignedRoutineId,
                isCompetence: vm.isCompetence
            }

            await sendMessage(payload, vm.coachId, vm.studentId)
        },
        setDefaults() {
            const vm = this;
            const postInfo = vm.currentRoutine?.advance?.postInfo || null
            vm.comment = postInfo?.comment || ''
            vm.duration = postInfo?.duration
            vm.effort = postInfo?.effort || '5'
            vm.position = postInfo?.position || ''
            vm.team = postInfo?.team || ''
        }
    },
    watch: {
        currentRoutine() {
            this.setDefaults()
        }
    }
}
</script>

<style scoped>
.last-step {
    max-width: 560px;
    margin: 0 auto;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 0 16px 24px;
}

.ls-card {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    padding: 16px 18px;
}

.ls-label {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--v-txtMuted-base);
    margin-bottom: 10px;
}

.finish-btn {
    border-radius: 12px;
    font-weight: 700;
    text-transform: none;
    letter-spacing: 0;
}
</style>